import React, { useState } from 'react';
import { mockData } from '../data/mock';
import { Button } from '../components/ui/button';
import OptimizedImage from '../components/OptimizedImage';

const Portfolio = () => {
  const { projects } = mockData;
  const [filter, setFilter] = useState('All');

  const categories = ['All', 'Event Conference', 'Wedding', 'Corporate', 'Talent Booking', 'Celebrations', 'Media Production', 'Celebrity Shoots', 'Luxury & Grandeur', 'Music Production'];

  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(project => project.category === filter);

  return (
    <div className="bg-dark-bg">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/PC15.jpg"
            alt="Portfolio"
            className="w-full h-full object-cover"
            priority={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <p className="text-gold text-sm tracking-widest uppercase mb-4">Our Work</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-silver leading-tight">
            Portfolio of
            <span className="gold-gradient-text block mt-2">Excellence</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto mt-6">
            Explore our collection of meticulously crafted events that showcase our commitment to perfection.
          </p>
          {/* Golden Decorative Line */}
          <div className="flex justify-center mt-8">
            <div className="relative w-96 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent shadow-lg" style={{
              boxShadow: '0 0 15px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 215, 0, 0.2)'
            }}>
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gold rounded-full shadow-lg" style={{
                boxShadow: '0 0 10px rgba(255, 215, 0, 0.6), 0 0 20px rgba(255, 215, 0, 0.3)'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-black border-b border-gold/20 sticky top-[88px] z-40">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setFilter(category)}
                variant="outline"
                className={`px-8 py-3 rounded-none transition-all duration-300 border-2 border-gold text-silver ${
                  filter === category
                    ? 'bg-gold/20 hover:bg-gold/30'
                    : 'hover:bg-gold/10'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div key={project.id} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-sm aspect-[3/4]">
                  <OptimizedImage
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-90 group-hover:opacity-95 transition-opacity"></div>
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gold text-xs tracking-wider uppercase font-semibold">
                        {project.category}
                      </span>
                    </div>
                    <h3 className="text-silver text-xl font-bold group-hover:text-gold transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-300 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {project.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-black border-t border-gold/20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Trusted by
              <span className="gold-gradient-text"> Leading Brands</span>
            </h2>
            <p className="text-gray-300 text-lg">
              From Fortune 500 companies to discerning private clients, we've had the privilege of creating 
              memorable experiences for Thailand's most prestigious events.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;